'use client';

import React, { useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface DeleteConfirmModalProps {
  onClose: () => void;
  onConfirm: () => void;
}

export default function DeleteConfirmModal({ onClose, onConfirm }: DeleteConfirmModalProps) {
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="delete-modal-title"
      aria-describedby="delete-modal-desc"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-enter bg-card rounded-xl shadow-modal w-full max-w-sm border border-border">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-danger/10 flex items-center justify-center">
              <AlertTriangle size={16} className="text-danger" />
            </div>
            <h2 id="delete-modal-title" className="text-base font-semibold text-foreground">
              Excluir Lançamento
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="
              p-1.5 rounded-md text-muted-foreground
              hover:text-foreground hover:bg-muted
              transition-all duration-150 active:scale-90
            "
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5">
          <p id="delete-modal-desc" className="text-sm text-foreground">
            Tem certeza que deseja excluir este lançamento?
          </p>
          <p className="text-sm text-muted-foreground mt-1.5">
            O saldo dos registros subsequentes será recalculado automaticamente. Esta ação não pode ser desfeita.
          </p>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="
              px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground
              hover:bg-muted transition-all duration-150 active:scale-95
            "
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="
              px-4 py-2 rounded-md bg-danger text-danger-foreground text-sm font-medium
              hover:opacity-90 transition-all duration-150 active:scale-95
            "
          >
            Excluir Lançamento
          </button>
        </div>
      </div>
    </div>
  );
}